'use client';

import {
  Button,
  ButtonGroup,
  CardTable,
  Checkbox,
  CellCheckbox,
  CardPagination,
  Flex,
  Text,
  Heading,
  EmptyState,
  Select,
} from '@/_components';
import { useMemo, useState, useContext, useCallback, type FC } from 'react';
import { TaxReturnCell } from './_components/TaxReturnCell';
import {
  ColumnDef,
  getSortedRowModel,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useTable,
  type PaginationState,
} from '@tanstack/react-table';
import { PAGINATION_OPTIONS } from '@/_constants/tables';
import { TAX_RETURN_TABLE_HEADER_MAP } from '@/_constants/tax-return';
import type { TaxReturnsTableProps } from './TaxReturnsTable.types';
import { AdminTaxReturnsContext } from '@/_contexts/admin/AdminTaxReturnsProvider';
import { TaxReturnDeliveryColumnType } from '@/_types/taxReturnDelivery';
import { TaxReturnDeliveryDialog } from '@/_components/TaxReturnDeliveryDialog';
import {
  updateTaxReturn,
  deleteTaxReturn,
  sendReminder,
} from '@/_api/admin-tax-returns';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { TaxReturn } from '@/_types/models/taxReturns';
import { getClasses } from '@/_utils/classnames';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { IdentityVerificationDialog } from '@/_components/IdentityVerificationDialog';
import {
  sortByPayments,
  sortBySignatures,
  sortByReminderDisabled,
} from './TaxReturnsTable.utils';
import { useRouter } from 'next/navigation';

interface ExtendedPaginationState extends PaginationState {
  totalPages: number;
  totalCount: number;
}

const SORTING_ENABLED_COLUMNS: Array<TaxReturnDeliveryColumnType> = [
  'company_name',
  'tax_year',
  'tax_return_name',
  'current_status',
  'payments',
  'updated_at',
  'last_signed_at',
  'last_reminder_sent_at',
  'assigned_advisor',
  'reminder_disabled',
  'identity_verification_status',
];

export const TaxReturnsTable: FC<TaxReturnsTableProps> = ({
  isInline,
  taxReturnsAlreadyLoaded,
}) => {
  const {
    getTaxReturns,
    hasInitiallyLoaded,
    isAdvisorLoading,
    isFiltering,
    pagination,
    resetFilters,
    selectedTaxReturns,
    setPagination,
    setSelectedTaxReturns,
    setSending,
    setSorting,
    sorting,
    taxReturns: taxReturnsFromContext,
    currentTaxReturnIdForVue,
    setCurrentTaxReturnIdForVue,
  } = useContext(AdminTaxReturnsContext);

  const taxReturns: Array<TaxReturn> = useMemo(
    () => taxReturnsAlreadyLoaded || taxReturnsFromContext || [],
    [taxReturnsAlreadyLoaded, taxReturnsFromContext],
  );

  const { push } = useRouter();
  const { token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);
  const { firmUsers, preferences } = useContext(FirmSettingsContext);

  const [selectedVerificationReturn, setSelectedVerificationReturn] =
    useState<TaxReturn | null>(null);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);

  const isIdentityVerificationEnabled = useMemo(
    () => preferences?.tax_return_stripe_idv_enabled,
    [preferences?.tax_return_stripe_idv_enabled],
  );

  const k1DistributionEnabled = useMemo(
    () => preferences?.tax_return_k1_distribution_enabled,
    [preferences?.tax_return_k1_distribution_enabled],
  );

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      ...(!isInline
        ? [
            {
              accessorKey: 'select',
              cell: () => null,
            },
          ]
        : []),
      {
        accessorKey: 'company_name',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'tax_year',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'tax_return_name',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'current_status',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'signatures',
        cell: (info) => info.getValue(),
        sortingFn: sortBySignatures,
      },
      {
        accessorKey: 'payments',
        cell: (info) => info.getValue(),
        sortingFn: sortByPayments,
      },
      {
        accessorKey: 'updated_at',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'last_reminder_sent_at',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'last_signed_at',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'assigned_advisor',
        cell: (info) => info.getValue(),
        accessorFn: (row) => {
          const advisor = firmUsers?.find(
            (fu) => fu.id === row.assigned_advisor_id,
          );
          return advisor?.name || '';
        },
      },
      {
        accessorKey: 'reminder_disabled',
        cell: (info) => info.getValue(),
        sortingFn: sortByReminderDisabled,
      },
      ...(isIdentityVerificationEnabled
        ? [
            {
              accessorKey: 'identity_verification_status',
              cell: (info: any) => info.getValue(),
            },
          ]
        : []),
      ...(k1DistributionEnabled
        ? [
            {
              accessorKey: 'k1_distribution_status',
              cell: (info: any) => info.getValue(),
            },
          ]
        : []),
      {
        accessorKey: 'actions',
        cell: (info) => info.getValue(),
      },
    ],
    [isIdentityVerificationEnabled, isInline, firmUsers, k1DistributionEnabled],
  );

  const currentPagination = useMemo<ExtendedPaginationState>(
    () => ({
      pageIndex: pagination?.pageIndex ?? 0,
      pageSize: pagination?.pageSize ?? 10,
      totalPages: pagination?.totalPages ?? 0,
      totalCount: pagination?.totalCount ?? 0,
    }),
    [pagination],
  );

  const table = useTable({
    columns,
    data: taxReturns,
    manualPagination: true,
    manualSorting: true,
    pageCount: currentPagination.totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: (updatedSorting: any) => {
      setSorting(updatedSorting);
      setPagination({
        pageIndex: 0,
        pageSize: currentPagination.pageSize,
        totalPages: currentPagination.totalPages,
        totalCount: currentPagination.totalCount,
      });
    },
    onPaginationChange: (updaterOrValue) => {
      const newPagination =
        typeof updaterOrValue === 'function'
          ? updaterOrValue({
              pageIndex: currentPagination.pageIndex,
              pageSize: currentPagination.pageSize,
            })
          : updaterOrValue;

      setPagination({
        pageIndex: newPagination.pageIndex,
        pageSize: newPagination.pageSize,
        totalPages: currentPagination.totalPages,
        totalCount: currentPagination.totalCount,
      });
    },
    state: {
      sorting,
      pagination: {
        pageIndex: currentPagination.pageIndex,
        pageSize: currentPagination.pageSize,
      },
    },
    autoResetPageIndex: false,
  });

  const currentTaxReturn: TaxReturn | undefined = useMemo(() => {
    return taxReturns?.find(
      (taxReturn) => taxReturn.id === currentTaxReturnIdForVue,
    );
  }, [taxReturns, currentTaxReturnIdForVue]);

  const _updateTaxReturn = useCallback(
    async (taxReturnId: number, companyId: number, data: any) => {
      await updateTaxReturn({
        id: taxReturnId,
        companyId,
        token,
        ...data,
      });
      await getTaxReturns(true);
    },
    [token, getTaxReturns],
  );

  const isAllVisibleSelected: boolean = useMemo(
    () =>
      taxReturns?.every(
        (q) => selectedTaxReturns?.find((tr) => tr.id === q.id)?.id,
      ),
    [selectedTaxReturns, taxReturns],
  );

  const onToggleSelectVisible = () => {
    if (isAllVisibleSelected) {
      const newSelectedTaxReturns: Array<TaxReturn> =
        selectedTaxReturns?.filter(
          (selectedTaxReturn) =>
            !taxReturns.some(
              (visibleQuestionnaire) =>
                visibleQuestionnaire.id === selectedTaxReturn.id,
            ),
        );

      setSelectedTaxReturns(newSelectedTaxReturns);
    } else {
      const newSelectedTaxReturns: Array<TaxReturn> = [
        ...new Set([...selectedTaxReturns, ...taxReturns]),
      ];

      setSelectedTaxReturns(newSelectedTaxReturns);
    }
  };

  const toggleTaxReturnSelection = (taxReturn?: TaxReturn) => {
    const isSelected: boolean = !!selectedTaxReturns?.find(
      (q) => q.id === taxReturn?.id,
    )?.id;

    const newSelectedTaxReturns: Array<TaxReturn> = isSelected
      ? selectedTaxReturns?.filter((q) => q.id !== taxReturn?.id)
      : taxReturn
        ? selectedTaxReturns.concat(taxReturn)
        : selectedTaxReturns;

    return setSelectedTaxReturns(newSelectedTaxReturns);
  };

  const _deleteTaxReturn = useCallback(
    async (taxReturnId: number, companyId: number) => {
      await deleteTaxReturn({
        id: taxReturnId,
        companyId,
        token,
      });

      await getTaxReturns(true);
    },
    [token, getTaxReturns],
  );

  const _sendReminder = useCallback(
    async (taxReturnId: number, companyId: number) => {
      const response = await sendReminder({
        id: taxReturnId,
        companyId,
        token,
      });
      if (!response.error && response.message === 'Success') {
        addToast('success', 'Reminders sent successfully to all recipients.');
      } else {
        addToast('error', 'Failed to send reminders.');
      }
      await getTaxReturns(true);
    },
    [token, addToast, getTaxReturns],
  );

  const showTaxReturn = useCallback(
    (taxReturnId: number) => {
      const taxReturn: TaxReturn | undefined = taxReturns?.find(
        (taxReturn) => taxReturn.id === taxReturnId,
      );

      if (!taxReturn) {
        return addToast('error', 'Tax return not found.', {
          error: { taxReturns, taxReturnId },
        });
      }

      if (taxReturn.status === 'processing') {
        return addToast(
          'info',
          'The tax return is still processing. It may take up to a few minutes, depending on the size of the return.',
        );
      }

      if (!preferences?._tmp_new_delivery_enabled) {
        return setCurrentTaxReturnIdForVue(taxReturnId);
      }

      if (taxReturn.current_status === 'action_required') {
        push(
          `/admin/${taxReturn.company_hash}/tax-returns/${taxReturnId}/classifications`,
        );
      } else if (taxReturn.draft_status !== 'draft') {
        push(`/admin/${taxReturn.company_hash}/tax-returns/${taxReturnId}`);
      }
    },
    [
      addToast,
      taxReturns,
      push,
      preferences?._tmp_new_delivery_enabled,
      setCurrentTaxReturnIdForVue,
    ],
  );

  const handleOpenVerification = (taxReturn: TaxReturn) => {
    setSelectedVerificationReturn(taxReturn);
    setShowVerificationDialog(true);
  };

  if (hasInitiallyLoaded && !taxReturns?.length) {
    return (
      <CardTable isInline={isInline} isLoading={false}>
        <EmptyState description="No tax returns were found" icon="file">
          <ButtonGroup align="center">
            {isFiltering ? (
              <Button
                intent="secondary"
                iconLeft="x"
                onClick={resetFilters}
                value="Reset filters"
              />
            ) : (
              <Button
                intent="primary"
                iconRight="arrow-bend-up-right"
                onClick={() => setSending(true)}
                value="Send Tax Return"
              />
            )}
          </ButtonGroup>
        </EmptyState>
      </CardTable>
    );
  }

  const start: number =
    currentPagination.pageIndex * currentPagination.pageSize + 1;
  const end: number = Math.min(
    (currentPagination.pageIndex + 1) * currentPagination.pageSize,
    currentPagination.totalCount,
  );
  const rowRange: string = `${start}-${end} of ${currentPagination.totalCount}`;

  return (
    <>
      {currentTaxReturn && (
        <TaxReturnDeliveryDialog
          taxReturn={currentTaxReturn}
          onClose={() => setCurrentTaxReturnIdForVue(null)}
        />
      )}
      <CardTable
        isInline={isInline}
        isLoading={isAdvisorLoading && !hasInitiallyLoaded}
      >
        <table
          className="sorabanTable"
          data-testid="TaxReturnsTable-deliver-table"
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
                  const isLastColumn: boolean =
                    index === headerGroup.headers.length - 1;
                  const isSortingEnabled: boolean =
                    SORTING_ENABLED_COLUMNS.includes(
                      header.column.id as TaxReturnDeliveryColumnType,
                    );
                  const thClasses: string = getClasses({
                    sorabanTable__fixedColumn: isLastColumn,
                    extraPadding: isInline,
                  });

                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={thClasses}
                      style={{
                        width:
                          header.id === 'select' ? '32px' : header.getSize(),
                      }}
                    >
                      {header.isPlaceholder ? null : (
                        <Flex
                          isYCentered={true}
                          gap={2}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {!isInline && header.id === 'select' ? (
                            <CellCheckbox isHeader={true}>
                              <Checkbox
                                label="Select"
                                isLabelHidden={true}
                                isChecked={isAllVisibleSelected}
                                onChange={onToggleSelectVisible}
                              />
                            </CellCheckbox>
                          ) : (
                            <Heading level={7} isSameLine={true}>
                              {
                                TAX_RETURN_TABLE_HEADER_MAP[
                                  header.column
                                    .id as TaxReturnDeliveryColumnType
                                ]
                              }
                            </Heading>
                          )}
                          {isSortingEnabled && (
                            <ButtonGroup isSameLine={true}>
                              {{
                                asc: (
                                  <Button
                                    value="x"
                                    intent="ghost"
                                    size="tiny"
                                    isIconOnly={true}
                                    iconLeft="arrow-up"
                                  />
                                ),
                                desc: (
                                  <Button
                                    value="x"
                                    intent="ghost"
                                    size="tiny"
                                    isIconOnly={true}
                                    iconLeft="arrow-down"
                                  />
                                ),
                              }[header.column.getIsSorted() as string] ?? (
                                <Button
                                  value="minus"
                                  intent="ghost"
                                  size="tiny"
                                  isIconOnly={true}
                                  iconLeft="minus"
                                />
                              )}
                            </ButtonGroup>
                          )}
                        </Flex>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => {
              return (
                <tr key={row.id} data-testid="TaxReturnsTable-tax-return-row">
                  {row.getVisibleCells().map((cell, cellIndex) => {
                    const taxReturn: TaxReturn = cell?.row?.original;
                    const isLastColumn: boolean =
                      cellIndex === row.getVisibleCells().length - 1;

                    const isSelected: boolean = !!selectedTaxReturns?.find(
                      (q) => q.id === taxReturn?.id,
                    )?.id;

                    return (
                      <TaxReturnCell
                        key={cell.id}
                        isLastColumn={isLastColumn}
                        isInline={isInline}
                        type={cell.column.id as TaxReturnDeliveryColumnType}
                        taxReturn={cell.row.original}
                        isSelected={isSelected}
                        toggleTaxReturnSelection={toggleTaxReturnSelection}
                        showTaxReturn={showTaxReturn}
                        updateTaxReturn={_updateTaxReturn}
                        deleteTaxReturn={_deleteTaxReturn}
                        sendReminders={_sendReminder}
                        onOpenVerification={handleOpenVerification}
                        hasExtraPadding={isInline}
                      />
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardTable>
      <CardPagination isHidden={isInline}>
        <Flex gap={4} isColumn={true}>
          <ButtonGroup align="center">
            <Button
              intent="ghost"
              size="tiny"
              value="<<"
              isIconOnly={true}
              iconLeft="caret-double-left"
              onClick={() =>
                setPagination({
                  pageIndex: 0,
                  pageSize: currentPagination.pageSize,
                  totalPages: currentPagination.totalPages,
                  totalCount: currentPagination.totalCount,
                })
              }
              isDisabled={currentPagination.pageIndex <= 0}
            />
            <Button
              intent="ghost"
              size="tiny"
              value="<"
              isIconOnly={true}
              iconLeft="caret-left"
              onClick={() =>
                setPagination({
                  pageIndex: Math.max(0, currentPagination.pageIndex - 1),
                  pageSize: currentPagination.pageSize,
                  totalPages: currentPagination.totalPages,
                  totalCount: currentPagination.totalCount,
                })
              }
              isDisabled={currentPagination.pageIndex <= 0}
            />
            <Select
              selectedValue={String(currentPagination.pageSize)}
              label="Page Size"
              isLabelHidden={true}
              items={PAGINATION_OPTIONS}
              onChange={(e) => {
                const newSize = Number(e.target.value);
                setPagination({
                  pageIndex: 0,
                  pageSize: newSize,
                  totalPages: currentPagination.totalPages,
                  totalCount: currentPagination.totalCount,
                });
              }}
            />
            <Button
              intent="ghost"
              size="tiny"
              value=">"
              isIconOnly={true}
              iconRight="caret-right"
              onClick={() =>
                setPagination({
                  pageIndex: Math.min(
                    currentPagination.totalPages - 1,
                    currentPagination.pageIndex + 1,
                  ),
                  pageSize: currentPagination.pageSize,
                  totalPages: currentPagination.totalPages,
                  totalCount: currentPagination.totalCount,
                })
              }
              isDisabled={
                currentPagination.pageIndex >= currentPagination.totalPages - 1
              }
            />
            <Button
              intent="ghost"
              size="tiny"
              isIconOnly={true}
              value=">>"
              iconLeft="caret-double-right"
              onClick={() =>
                setPagination({
                  pageIndex: currentPagination.totalPages - 1,
                  pageSize: currentPagination.pageSize,
                  totalPages: currentPagination.totalPages,
                  totalCount: currentPagination.totalCount,
                })
              }
              isDisabled={
                currentPagination.pageIndex >= currentPagination.totalPages - 1
              }
            />
          </ButtonGroup>
          <Flex xAlign="center">
            <Text type="micro" color="secondary">
              {rowRange}
            </Text>
          </Flex>
        </Flex>
      </CardPagination>
      {showVerificationDialog && selectedVerificationReturn && (
        <IdentityVerificationDialog
          isOpen={showVerificationDialog}
          onClose={() => {
            setShowVerificationDialog(false);
            setSelectedVerificationReturn(null);
          }}
          companyName={selectedVerificationReturn.company_name}
          signers={selectedVerificationReturn.signers ?? []}
          verifications={
            selectedVerificationReturn.stripe_identity_verifications
          }
        />
      )}
    </>
  );
};
